"use client";

import { useState, useMemo, useRef } from "react";
import { clsx } from "clsx";
import * as m from "motion/react-m";
import { flushSync } from "react-dom";
import type { Key } from "react-aria-components";
import { Button, SelectValue, Select as RACSelect } from "react-aria-components";
import type { Voice } from "@mw/shared/payload-types";
import { OrbWithMessages } from "@ui/components/core/Orb/OrbWithMessages";
import { useAgentSession } from "@/app/_blocks/BlockInteractCreateOmnibox/demos/Agents/useAgentSession";
import { AgentCallButton } from "@/app/_blocks/BlockInteractCreateOmnibox/demos/Agents/AgentCallButton";
import { AgentTermsOverlay } from "@/app/_blocks/BlockInteractCreateOmnibox/demos/Agents/AgentTermsOverlay";
import { useTranslations } from "next-intl";
import { getLocalStorage, LocalStorageKeys, setLocalStorage } from "@utilities/localStorage";
import { getImageProps } from "@utilities/payload";
import { ListBox, type ItemBase } from "@ui/components/core/ListBox";
import IconChevronDownSmall from "@ui/central-icons/icons/IconChevronDownSmall";
import { iconStrokeFix } from "@ui/central-icons/iconUtils";
import Image from "next/image";

export interface AgentOrbDemoProps {
  agentId: string;
  voices: Voice[];
}

interface VoiceItem extends ItemBase {
  voiceId: string;
}

export function AgentOrbDemo({ agentId, voices }: AgentOrbDemoProps) {
  const t = useTranslations("ClientKeys.ConvAI");
  const [selectedVoiceId, setSelectedVoiceId] = useState(voices[0]?.voiceId);
  const [showTermsFor, setShowTermsFor] = useState<"voice" | null>(null);
  const pendingRestartRef = useRef(false);

  const session = useAgentSession({
    agentId,
    voiceId: selectedVoiceId,
    defaultMode: "voice",
    onDisconnect: () => {
      if (pendingRestartRef.current) {
        pendingRestartRef.current = false;
        setTimeout(() => startSessionWithTermsCheck(), 0);
      }
    },
  });

  const voiceItems = useMemo(
    () =>
      voices.map((voice): VoiceItem => {
        const category = voice.category || "Other";
        const imgProps = getImageProps(voice.image);
        return {
          id: voice.voiceId,
          voiceId: voice.voiceId,
          textValue: category,
          label: category,
          image: imgProps ? { src: imgProps.src, width: imgProps.width, height: imgProps.height } : undefined,
        };
      }),
    [voices],
  );

  const selectedVoice = useMemo(() => voices.find(v => v.voiceId === selectedVoiceId), [voices, selectedVoiceId]);

  const selectedImageProps = useMemo(
    () => (selectedVoice ? getImageProps(selectedVoice.image) : null),
    [selectedVoice],
  );

  function startSessionWithTermsCheck() {
    const termsAccepted = getLocalStorage(LocalStorageKeys.CONVAI_MARKETING_PAGE_TERMS);
    if (termsAccepted) {
      session.startVoiceSession();
    } else {
      setShowTermsFor("voice");
    }
  }

  function handleCallToggle() {
    if (session.sessionIsActive) {
      session.endSession();
    } else if (session.status === "disconnected") {
      startSessionWithTermsCheck();
    }
  }

  function handleVoiceChange(key: Key | null) {
    if (key === null) return;
    const voiceId = String(key);
    if (voiceId === selectedVoiceId) return;
    setSelectedVoiceId(voiceId);
    if (session.sessionIsActive) {
      pendingRestartRef.current = true;
      session.endSession();
    }
  }

  return (
    <div className='tw-relative tw-h-full tw-flex tw-flex-col tw-items-center tw-justify-center'>
      <m.div
        initial={false}
        animate={showTermsFor === null ? { opacity: 1, transition: { delay: 0.15 } } : { opacity: 0 }}
        inert={showTermsFor !== null}
        className='tw-max-w-full tw-flex tw-flex-col tw-items-center'
      >
        <OrbWithMessages
          messages={session.messages}
          mode='voice'
          texture='agents-1'
          orbSize='default'
          conversation={session.conversation}
          agentMessageStyle='warm'
        />
        <div className='tw-relative tw-flex tw-items-center tw-gap-2 tw-mt-6'>
          <AgentCallButton
            sessionIsActive={session.sessionIsActive}
            onToggle={handleCallToggle}
            className='!tw-size-11'
          />
          <RACSelect selectedKey={selectedVoiceId} onSelectionChange={handleVoiceChange} aria-label={t("select_voice")}>
            {({ isOpen }) => (
              <>
                <Button
                  className={({ isFocusVisible }) =>
                    clsx(
                      "tw-text-sm/none tw-font-medium tw-w-48 tw-text-black tw-flex tw-items-center tw-gap-2 tw-h-11 tw-pl-2 tw-pr-2.5 tw-rounded-full tw-bg-white hover:tw-bg-gray-50 tw-transition-colors",
                      isFocusVisible ? "tw-outline-focus" : "tw-outline-none",
                    )
                  }
                  style={{ boxShadow: "0 0 1px rgb(0 0 0 / 0.4), 0 4px 4px rgb(0 0 0 / 0.04)" }}
                >
                  {({ isHovered }) => (
                    <>
                      {selectedImageProps && (
                        <Image
                          src={selectedImageProps.src}
                          width={selectedImageProps.width}
                          height={selectedImageProps.height}
                          alt=''
                          className='tw-size-6 tw-rounded-full tw-object-cover tw-flex-none'
                          sizes='1.5rem'
                        />
                      )}
                      <SelectValue className='tw-truncate tw-flex-1 tw-text-left tw-min-w-0'>
                        {() => selectedVoice?.category ?? selectedVoice?.name}
                      </SelectValue>
                      <IconChevronDownSmall
                        className={clsx(
                          iconStrokeFix,
                          "tw-size-4 tw-flex-none tw-transition",
                          isOpen || isHovered ? "tw-text-black" : "tw-text-gray-400",
                        )}
                      />
                    </>
                  )}
                </Button>
                <ListBox
                  items={voiceItems}
                  selectionMode='single'
                  aria-label={t("voices_list")}
                  style={{ minWidth: "calc(max(11rem, var(--trigger-width) + 0.5rem) + 2rem)" }}
                  offset={8}
                  crossOffset={-4}
                />
              </>
            )}
          </RACSelect>
        </div>
      </m.div>
      <AgentTermsOverlay
        showTermsFor={showTermsFor}
        onAgree={() => {
          setLocalStorage(LocalStorageKeys.CONVAI_MARKETING_PAGE_TERMS, "true");
          flushSync(() => {
            setShowTermsFor(null);
          });
          session.startVoiceSession();
        }}
        onClose={() => setShowTermsFor(null)}
      />
    </div>
  );
}
